import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { RestoreResult } from '@/common/soft-delete';
import { t } from '@/i18n';
import { describeAlsoAffected, invalidateAfterSoftDelete } from '@/lib/utils/soft-delete';
import { toastApiError } from '@/queries/common';
import { restoreItem, type SoftDeleteTarget, softDeleteItem } from '@/queries/soft-delete/soft-delete';

type DeleteVariables = {
	target: SoftDeleteTarget;
	/** The item's name, for the restore toast of Undo */
	name: string;
	acceptWorkPackageImpact?: boolean;
	/** The success toast, e.g. "Asset deleted" */
	successMessage: string;
};

type RestoreVariables = { target: SoftDeleteTarget; name: string };

function toastRestored(result: RestoreResult, { target, name }: RestoreVariables) {
	const items = describeAlsoAffected(result.restored, target.type);
	toast.success(items ? t('common.deleted.restoredWith', { name, items }) : t('common.deleted.restored', { name }));
}

/** Restores an item (and everything deleted with it) and refreshes every list it can appear in. */
async function restoreAndRefresh(queryClient: QueryClient, variables: RestoreVariables) {
	const result = await restoreItem(variables.target);
	await invalidateAfterSoftDelete(queryClient, [variables.target.id]);
	return result;
}

/**
 * Soft delete with an "Undo" action on the success toast (GH #331). Undo restores exactly that
 * delete, so it needs no second confirmation.
 */
export function useSoftDeleteItem({ onDeleted }: { onDeleted?: (variables: DeleteVariables) => void } = {}) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ target, acceptWorkPackageImpact }: DeleteVariables) =>
			softDeleteItem(target, { acceptWorkPackageImpact }),
		onSuccess: async (_data, variables) => {
			await invalidateAfterSoftDelete(queryClient, [variables.target.id]);
			toast.success(variables.successMessage, {
				action: {
					label: t('common.deleted.undo'),
					onClick: () => {
						restoreAndRefresh(queryClient, variables)
							.then((result) => toastRestored(result, variables))
							.catch(toastApiError);
					},
				},
			});
			onDeleted?.(variables);
		},
		onError: toastApiError,
	});
}

/** Restore from a deleted-items list. */
export function useRestoreItem({ onRestored }: { onRestored?: (variables: RestoreVariables) => void } = {}) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (variables: RestoreVariables) => restoreAndRefresh(queryClient, variables),
		onSuccess: (result, variables) => {
			toastRestored(result, variables);
			onRestored?.(variables);
		},
		onError: toastApiError,
	});
}
